// ==================== //
// Product Page Specific JavaScript
// ==================== //

// Enhanced error handling utilities (shared with main app.js)
// Retry mechanism with exponential backoff
async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = attempt === maxRetries - 1;
      if (isLastAttempt) {
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

// Fetch with timeout
async function fetchWithTimeout(url, options = {}, timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please check your internet connection');
    }
    throw error;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  initProductAnimations();
  initProductScrollEffects();
  initProductCTAs();
});

// ==================== //
// Product Page Animations
// ==================== //
function initProductAnimations() {
  // Animate benefit metrics on scroll
  const benefitMetrics = document.querySelectorAll('.benefit-metric');

  const metricsObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.animation = 'metricCount 1.5s ease-out';
        metricsObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  benefitMetrics.forEach(metric => {
    metricsObserver.observe(metric);
  });

  // Animate workflow steps
  const workflowSteps = document.querySelectorAll('.workflow-step');

  const stepsObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateX(0)';
        }, index * 100);
        stepsObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  workflowSteps.forEach(step => {
    step.style.opacity = '0';
    step.style.transform = 'translateX(-30px)';
    step.style.transition = 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)';
    stepsObserver.observe(step);
  });

  // Animate problem cards
  const problemCards = document.querySelectorAll('.problem-card');

  const problemObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }, index * 150);
        problemObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  problemCards.forEach(card => {
    card.style.opacity = '0';
    card.style.transform = 'translateY(30px)';
    card.style.transition = 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)';
    problemObserver.observe(card);
  });

  // Animate feature cards
  const featureCards = document.querySelectorAll('.feature-card');

  const featureObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateX(0)';
        }, index * 200);
        featureObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  featureCards.forEach(card => {
    card.style.opacity = '0';
    card.style.transform = 'translateX(-40px)';
    card.style.transition = 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)';
    featureObserver.observe(card);
  });

  // Animate mini feature cards
  const miniFeatureCards = document.querySelectorAll('.mini-feature-card');

  const miniObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'scale(1)';
        }, index * 80);
        miniObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  miniFeatureCards.forEach(card => {
    card.style.opacity = '0';
    card.style.transform = 'scale(0.9)';
    card.style.transition = 'all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)';
    miniObserver.observe(card);
  });

  // Animate pricing cards
  const pricingCards = document.querySelectorAll('.pricing-card-product');

  const pricingObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0) scale(1)';
        }, index * 150);
        pricingObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  pricingCards.forEach(card => {
    // Don't affect the featured plan's initial scale
    if (card.classList.contains('featured-plan')) {
      card.style.opacity = '0';
      card.style.transform = 'translateY(30px) scale(1.05)';
    } else {
      card.style.opacity = '0';
      card.style.transform = 'translateY(30px) scale(1)';
    }
    card.style.transition = 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)';
    pricingObserver.observe(card);
  });
}

// ==================== //
// Product Scroll Effects
// ==================== //
function initProductScrollEffects() {
  // Parallax effect for product hero
  const productHero = document.querySelector('.product-hero');
  if (!productHero) return;

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const scrolled = window.pageYOffset;
        const heroContent = productHero.querySelector('.hero-content');

        if (scrolled < window.innerHeight) {
          heroContent.style.transform = `translateY(${scrolled * 0.3}px)`;
          heroContent.style.opacity = Math.max(0.2, 1 - (scrolled / window.innerHeight) * 0.7);
        }

        ticking = false;
      });

      ticking = true;
    }
  }, { passive: true });

  // Animate sections on scroll
  const sections = document.querySelectorAll('.section');

  const sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'translateY(0)';
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

  sections.forEach(section => {
    section.style.opacity = '0';
    section.style.transform = 'translateY(20px)';
    section.style.transition = 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)';
    sectionObserver.observe(section);
  });
}

// ==================== //
// Product CTAs & Interactions
// ==================== //
function initProductCTAs() {
  // Smooth scroll to pricing section
  const pricingLinks = document.querySelectorAll('a[href="#pricing"]');

  pricingLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        const offsetTop = pricingSection.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    });
  });

  // Smooth scroll to demo section
  const demoLinks = document.querySelectorAll('a[href="#demo"]');

  demoLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const demoSection = document.getElementById('demo');
      if (demoSection) {
        const offsetTop = demoSection.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    });
  });

  // Track product CTA clicks with Google Analytics
  const ctaButtons = document.querySelectorAll('.btn-primary, .btn-secondary');

  ctaButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      const buttonText = button.textContent.trim();
      const section = button.closest('.section')?.querySelector('.section-title')?.textContent || 'Unknown';

      // Track with Google Analytics
      if (typeof gtag === 'function') {
        gtag('event', 'product_cta_click', {
          event_category: 'product_engagement',
          event_label: buttonText,
          button_text: buttonText,
          section_name: section,
          page_location: window.location.pathname
        });
      }
    });
  });

  // Track product page-specific sections visibility
  const productSections = [
    { selector: '.product-hero', name: 'product_hero' },
    { selector: '#pricing', name: 'product_pricing' },
    { selector: '#demo', name: 'product_demo' },
    { selector: '.workflow-section', name: 'product_workflow' },
    { selector: '.features-section', name: 'product_features' }
  ];

  productSections.forEach(({ selector, name }) => {
    const section = document.querySelector(selector);
    if (section) {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (typeof gtag === 'function') {
              gtag('event', 'product_section_view', {
                event_category: 'product_engagement',
                event_label: name,
                section_name: name,
                page_location: window.location.pathname
              });
            }
            sectionObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.3 });
      sectionObserver.observe(section);
    }
  });

  // Add hover effect to step icons
  const stepIcons = document.querySelectorAll('.step-icon');

  stepIcons.forEach(icon => {
    icon.addEventListener('mouseenter', () => {
      icon.style.transform = 'scale(1.1) rotate(5deg)';
    });

    icon.addEventListener('mouseleave', () => {
      icon.style.transform = 'scale(1) rotate(0deg)';
    });
  });

  // Add magnetic effect to pricing cards + track interactions
  const pricingCards = document.querySelectorAll('.pricing-card-product');

  pricingCards.forEach(card => {
    // Track pricing card clicks (HIGH PRIORITY - conversion signal)
    card.addEventListener('click', () => {
      const planName = card.querySelector('h3')?.textContent || 'unknown';
      const isFeatured = card.classList.contains('featured-plan');

      if (typeof gtag === 'function') {
        gtag('event', 'product_pricing_click', {
          event_category: 'product_conversion',
          event_label: planName,
          plan_name: planName,
          is_featured: isFeatured,
          page_location: window.location.pathname
        });
      }
    });

    // Track pricing card visibility
    const pricingObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const planName = entry.target.querySelector('h3')?.textContent || 'unknown';
          const isFeatured = entry.target.classList.contains('featured-plan');

          if (typeof gtag === 'function') {
            gtag('event', 'product_pricing_view', {
              event_category: 'product_engagement',
              event_label: planName,
              plan_name: planName,
              is_featured: isFeatured,
              page_location: window.location.pathname
            });
          }
          pricingObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    pricingObserver.observe(card);

    // Magnetic effect
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      // Subtle tilt effect
      const tiltX = (y / rect.height) * 5;
      const tiltY = (x / rect.width) * -5;

      if (card.classList.contains('featured-plan')) {
        card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-8px) scale(1.05)`;
      } else {
        card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-8px)`;
      }
    });

    card.addEventListener('mouseleave', () => {
      if (card.classList.contains('featured-plan')) {
        card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1.05)';
      } else {
        card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
      }
    });
  });

  // Track feature card visibility (which features interest users?)
  document.querySelectorAll('.feature-card').forEach(card => {
    const featureObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const featureName = entry.target.querySelector('h3')?.textContent || 'unknown';

          if (typeof gtag === 'function') {
            gtag('event', 'product_feature_view', {
              event_category: 'product_engagement',
              event_label: featureName,
              feature_name: featureName,
              page_location: window.location.pathname
            });
          }
          featureObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    featureObserver.observe(card);
  });

  // Track problem card visibility (pain point resonance)
  document.querySelectorAll('.problem-card').forEach(card => {
    const problemObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const problemText = entry.target.querySelector('h3, p')?.textContent?.substring(0, 50) || 'unknown';

          if (typeof gtag === 'function') {
            gtag('event', 'product_problem_view', {
              event_category: 'product_engagement',
              event_label: problemText,
              page_location: window.location.pathname
            });
          }
          problemObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    problemObserver.observe(card);
  });
}

// ==================== //
// Counter Animation for Metrics
// ==================== //
function animateCounter(element, target, duration = 2000) {
  const start = 0;
  const increment = target / (duration / 16); // 60fps
  let current = start;

  const timer = setInterval(() => {
    current += increment;
    if (current >= target) {
      element.textContent = target;
      clearInterval(timer);
    } else {
      element.textContent = Math.floor(current);
    }
  }, 16);
}

// Observe benefit metrics and animate counters
const benefitMetrics = document.querySelectorAll('.benefit-metric');
const counterObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const text = entry.target.textContent;
      const match = text.match(/\d+/);
      if (match) {
        const number = parseInt(match[0]);
        animateCounter(entry.target, number);
      }
      counterObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.5 });

benefitMetrics.forEach(metric => {
  counterObserver.observe(metric);
});

// ==================== //
// Contact Form Handler
// ==================== //
// EmailJS Configuration (shared with main site)
const EMAILJS_CONFIG = {
  publicKey: 'pAUYTzAFVr9G-o4jD',
  serviceId: 'service_shr97w8',
  businessTemplateId: 'template_8ssu6d6'
};

// Domain validation for EmailJS security
// Defense-in-depth: Verify forms are only submitted from authorized domains
function validateEmailJSDomain() {
  const allowedDomains = [
    'adoghri.com',
    'www.adoghri.com',
    'ideaboundary.com',
    'www.ideaboundary.com',
    'localhost',
    '127.0.0.1'
  ];

  const currentHostname = window.location.hostname;

  // Check if current domain is in the allowed list
  const isAllowed = allowedDomains.some(domain =>
    currentHostname === domain || currentHostname.endsWith('.' + domain)
  );

  if (!isAllowed) {
    return false;
  }

  return true;
}

// Google Sheets Configuration
const GOOGLE_SHEETS_CONFIG = {
  webAppUrl: 'https://script.google.com/macros/s/AKfycbxHnv59KMCH4u-WUYPsgU2434cbvIcwV3H8Fkqr2b_fk-YI7CPMkzVEjKHKavTEp1At/exec'
};

// ==================== //
// Google Sheets Integration - ENHANCED: With retry logic
// ==================== //
async function saveProductLeadToGoogleSheets(email, message) {
  const timestamp = new Date().toISOString();

  try {
    // Use retry mechanism for better reliability
    await retryWithBackoff(async () => {
      await fetchWithTimeout(
        GOOGLE_SHEETS_CONFIG.webAppUrl,
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            timestamp: timestamp,
            email: email,
            message: message,
            product: 'ResearchPilot',
            source: 'Product Page Contact Form'
          })
        },
        8000 // 8 second timeout
      );

    }, 2, 1000); // 2 retries with 1 second base delay

    return true;
  } catch (error) {
    // Don't block form submission if Sheets fails - graceful degradation
    return false;
  }
}

const productContactForm = document.getElementById('product-contact-form');
if (productContactForm) {
  productContactForm.addEventListener('submit', async (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    const email = (formData.get('email') || '').trim();
    const message = (formData.get('message') || '').trim();

    // Basic validation
    if (!email || !message) {
      alert('Please fill in both email and message fields.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Check honeypot (spam prevention)
    const honeypot = formData.get('_honey');
    if (honeypot) {
      return; // Silent fail for bots
    }

    // Show loading state
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';
    }

    try {
      // Validate domain (defense-in-depth security measure)
      if (!validateEmailJSDomain()) {
        throw new Error('This form can only be submitted from authorized domains.');
      }

      // Initialize EmailJS
      if (typeof emailjs !== 'undefined') {
        emailjs.init(EMAILJS_CONFIG.publicKey);
      } else {
        throw new Error('EmailJS library not loaded. Please refresh the page and try again.');
      }

      // Prepare template parameters for business notification
      const templateParams = {
        to_email: 'contact@ideaboundary.com',
        from_name: 'ResearchPilot Product Inquiry',
        user_email: email,
        message: message,
        product: 'ResearchPilot',
        timestamp: new Date().toISOString(),
        reply_to: email,
        _subject: 'New ResearchPilot Product Inquiry'
      };

      // Send email notification with retry logic for better reliability
      await retryWithBackoff(async () => {
        await emailjs.send(
          EMAILJS_CONFIG.serviceId,
          EMAILJS_CONFIG.businessTemplateId,
          templateParams
        );
      }, 2, 1500); // 2 retries with 1.5 second base delay

      // Save to Google Sheets (non-blocking, graceful degradation)
      saveProductLeadToGoogleSheets(email, message).catch((error) => {
        console.warn('Non-critical: Failed to save product lead:', error);
        // Continue anyway - this is not critical for user experience
      });

      // Success - show message and reset form
      alert('Thanks for reaching out! We\'ll get back to you within 24 hours.\n\nIn the meantime, feel free to book a demo directly from the page.');
      form.reset();

    } catch (error) {
      console.error('Error sending email:', error);

      // Provide specific error messages based on error type
      let errorMessage = 'Sorry, there was an error sending your message. ';

      if (error.message && error.message.includes('library not loaded')) {
        errorMessage += 'Please refresh the page and try again.';
      } else if (error.message && error.message.includes('timeout')) {
        errorMessage += 'The request timed out. Please check your internet connection and try again.';
      } else if (error.message && error.message.includes('authorized domains')) {
        errorMessage += 'Security validation failed. Please contact us directly at contact@ideaboundary.com';
      } else {
        errorMessage += 'Please try booking a demo instead or email us directly at contact@ideaboundary.com';
      }

      alert(errorMessage);
    } finally {
      // Restore button state
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText || 'Send Message';
      }
    }
  });
}

